import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { parseISO } from "date-fns"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { cn } from "design-system/utils"
import config from "lib/config"
import { formatDate } from "lopermut"
import { ChevronDown, ChevronRight, Download, ExternalLink } from "lucide-react"
import { useConversationsQuery } from "modules/conversations/hooks/useConversationsQuery"
import { useMessages } from "modules/messages/hooks/useMessages"
import { Loading } from "modules/shared/components/Loading"
import { ElementType, useEffect, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  ConversationFlowEnum,
  DataResponse,
  PromptEvaluationConversationSnapshotMetadataResponseDataSchema,
  PromptEvaluationConversationSnapshotResponseDataSchema,
  ConversationResponseDataSchema,
  ConversationStatusEnum,
  ConversationTypeEnum,
} from "schema"
import z from "zod"

type Tool = {
  id: string
  label: string
  icon: ElementType
  href: string | undefined
}

type ExpandedContentProps = {
  id: string
  isFailed: boolean
  failedReason: string | null | undefined
  isOutbound: boolean
  tools: Tool[]
  isMessageKindsExpanded: boolean
  onToggleMessageKinds: () => void
  conversationExperiments: z.infer<typeof ConversationResponseDataSchema>["conversationExperiments"]
}

type SnapshotMetadata = z.infer<
  typeof PromptEvaluationConversationSnapshotMetadataResponseDataSchema
>

type Props = {
  customerId: string
  conversationId?: string | null
}

export function Developer({ customerId, conversationId }: Props) {
  const { t } = useTranslation()

  const { data: conversations, isPending } = useConversationsQuery({
    query: {
      filterBy: {
        customerId,
      },
      sortBy: { field: "createdAt", order: "asc" },
      includeExperiments: true,
    },
  })

  const bottomRef = useRef<HTMLDivElement>(null)

  const [expandedId, setExpandedId] = useState<string | undefined>(() => {
    return conversationId ?? undefined
  })
  const [expandedMessageKinds, setExpandedMessageKinds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (conversationId) {
      setExpandedId(conversationId)
    }
  }, [conversationId])

  useEffect(() => {
    if (!isPending && conversations?.data.length) {
      bottomRef.current?.scrollIntoView({ behavior: "instant" })
    }
  }, [isPending, conversations?.data.length])

  const toggleMessageKinds = (conversationId: string) => {
    setExpandedMessageKinds((prev) => {
      const next = new Set(prev)
      if (next.has(conversationId)) {
        next.delete(conversationId)
      } else {
        next.add(conversationId)
      }
      return next
    })
  }

  const getTools = (conversationIdParam: string) => {
    const langsmithUrl =
      conversationIdParam && config.langsmith.projectId
        ? `https://smith.langchain.com/o/5f3112d7-fb88-4c2b-82b8-b77fb6be8b51/projects/p/${config.langsmith.projectId}/t/${conversationIdParam}`
        : undefined

    return [
      {
        id: "langsmith",
        label: t("customers.developer.tools.langsmith"),
        icon: ExternalLink,
        href: langsmithUrl,
      },
    ]
  }

  if (isPending) return <Loading />

  if (!conversations?.data.length) {
    return (
      <div className="flex flex-col gap-6 py-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">{t("customers.developer.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("customers.developer.description")}</p>
        </div>
        <p className="text-sm text-muted-foreground">{t("customers.developer.noConversations")}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold">{t("customers.developer.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("customers.developer.description")}</p>
      </div>
      <div className="flex flex-col gap-2">
        {conversations.data.map((conversation) => {
          const isExpanded = conversation.id === expandedId
          const isLive = conversation.type === ConversationTypeEnum.Live
          const isFailed = conversation.status === ConversationStatusEnum.Failed
          const isOutbound = conversation.conversationFlow === ConversationFlowEnum.Outbound
          const campaignName = conversation.campaign?.name || t("campaigns.untitled")
          const tools = getTools(conversation.id)
          const isMessageKindsExpanded = expandedMessageKinds.has(conversation.id)

          return (
            <div key={conversation.id} className="flex flex-col gap-2">
              <Button
                variant="ghost"
                className={cn(
                  "px-4 py-1.5 h-auto hover:bg-secondary justify-start",
                  isExpanded && "bg-secondary",
                )}
                aria-expanded={isExpanded}
                onClick={() => setExpandedId(isExpanded ? undefined : conversation.id)}
              >
                <div className="flex flex-row items-center justify-between gap-2 w-full">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-4">
                    <span className="text-sm font-medium">{campaignName}</span>
                    <div className="flex flex-row items-center gap-2 text-xs text-muted-foreground">
                      <span>{formatDate(parseISO(conversation.createdAt), "Pp")}</span>
                      <span>{"•"}</span>
                      <span>
                        {isLive ? t("conversations.types.Live") : t("conversations.types.Testing")}
                      </span>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="size-4 shrink-0" />
                  ) : (
                    <ChevronRight className="size-4 shrink-0" />
                  )}
                </div>
              </Button>
              {isExpanded && (
                <ConversationExpandedContent
                  id={conversation.id}
                  isFailed={isFailed}
                  failedReason={conversation.failedReason}
                  isOutbound={isOutbound}
                  tools={tools}
                  isMessageKindsExpanded={isMessageKindsExpanded}
                  onToggleMessageKinds={() => toggleMessageKinds(conversation.id)}
                  conversationExperiments={conversation.conversationExperiments}
                />
              )}
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  )
}

function ConversationExpandedContent({
  id,
  isFailed,
  failedReason,
  isOutbound,
  tools,
  isMessageKindsExpanded,
  onToggleMessageKinds,
  conversationExperiments,
}: ExpandedContentProps) {
  const { t } = useTranslation()
  const { messages } = useMessages(id)
  const [isSnapshotDialogOpen, setIsSnapshotDialogOpen] = useState(false)

  const firstMessage = messages[0]
  const usedTemplate = isOutbound && firstMessage?.whatsappTemplate
  const messagesWithKind = messages.filter((msg) => msg.kind)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pl-4 py-2">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t("customers.developer.developerTools")}
          </span>
          {tools.map((tool) => {
            const Icon = tool.icon

            return (
              <Button
                key={tool.id}
                variant="ghost"
                size="sm"
                disabled={!tool.href}
                asChild={!!tool.href}
                className="justify-start h-auto py-2"
              >
                {tool.href ? (
                  <a href={tool.href} target="_blank" rel="noopener noreferrer">
                    <Icon className="size-5 shrink-0" />
                    {tool.label}
                  </a>
                ) : (
                  <>
                    <Icon className="size-5 shrink-0" />
                    {tool.label}
                  </>
                )}
              </Button>
            )
          })}
          <Button
            variant="ghost"
            size="sm"
            className="justify-start h-auto py-2"
            onClick={() => setIsSnapshotDialogOpen(true)}
          >
            <Download className="size-5 shrink-0" />
            {"Download prompt evaluation snapshot"}
          </Button>
        </div>
        <PromptEvaluationSnapshotDialog
          conversationId={id}
          open={isSnapshotDialogOpen}
          onOpenChange={setIsSnapshotDialogOpen}
        />
        {conversationExperiments && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t("customers.developer.conversationExperiment.title")}
            </span>
            <div>
              {conversationExperiments.map(({ experiment, domain }, index) => (
                <div
                  className={cn("pl-2 flex flex-col text-sm", index && "pt-2 border-t")}
                  key={[experiment, domain].join("-")}
                >
                  <span>
                    {t("customers.developer.conversationExperiment.name", {
                      name: experiment,
                    })}
                  </span>
                  <span>
                    {t("customers.developer.conversationExperiment.domain", {
                      domain: domain,
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          {t("customers.developer.forensicHelpers")}
        </span>
        {(isFailed && failedReason) || usedTemplate || messagesWithKind.length > 0 ? (
          <div className="flex flex-col gap-3 p-3 bg-muted rounded-sm">
            {isFailed && failedReason && (
              <div className="text-xs font-medium">
                <Trans
                  i18nKey="customers.developer.failedReason"
                  values={{ reason: failedReason }}
                  components={{
                    value: <span className="text-muted-foreground font-normal" />,
                  }}
                />
              </div>
            )}

            {usedTemplate && (
              <div className="flex flex-col gap-1 text-xs font-medium">
                <div>
                  <Trans
                    i18nKey="customers.developer.usedTemplate"
                    values={{ template: usedTemplate.name }}
                    components={{
                      value: <span className="text-muted-foreground font-mono font-normal" />,
                    }}
                  />
                </div>
                {usedTemplate.mediaFormat && (
                  <div>
                    <Trans
                      i18nKey="customers.developer.mediaFormat"
                      values={{ format: usedTemplate.mediaFormat }}
                      components={{
                        value: <span className="text-muted-foreground font-normal" />,
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {messagesWithKind.length > 0 && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={onToggleMessageKinds}
                  className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors"
                >
                  {isMessageKindsExpanded ? (
                    <ChevronDown className="size-3" />
                  ) : (
                    <ChevronRight className="size-3" />
                  )}
                  {t("customers.developer.messageKinds")} {"("}
                  {messagesWithKind.length}
                  {")"}
                </button>
                {isMessageKindsExpanded && (
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    {messagesWithKind.map((message, index) => (
                      <li key={message.id} className="text-xs text-muted-foreground">
                        <span className="font-mono">{message.kind}</span>
                        <span className="text-muted-foreground/60">
                          {" (#"}
                          {index + 1}
                          {")"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground p-3">
            {t("customers.developer.noForensicData")}
          </span>
        )}
      </div>
    </div>
  )
}

function PromptEvaluationSnapshotDialog({
  conversationId,
  open,
  onOpenChange,
}: {
  conversationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data, isPending } = useQuery({
    queryKey: ["promptEvaluationSnapshotMetadata", conversationId],
    enabled: open,
    queryFn: async () => {
      const response = await axios.get<DataResponse<SnapshotMetadata>>(
        `/promptEvaluations/conversations/${conversationId}/snapshot/metadata`,
      )
      return response.data.data
    },
  })
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSelectedMessageId(data?.recommendedLastMessageId ?? data?.messages.at(-1)?.sourceId ?? null)
  }, [data, open])

  const downloadSnapshot = async () => {
    if (!selectedMessageId) return

    const response = await axios.get<
      DataResponse<z.infer<typeof PromptEvaluationConversationSnapshotResponseDataSchema>>
    >(`/promptEvaluations/conversations/${conversationId}/snapshot`, {
      params: { lastMessageId: selectedMessageId },
    })
    const snapshot = response.data.data
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `prompt-eval-conversation-${conversationId}-${selectedMessageId}.json`
    link.click()
    URL.revokeObjectURL(url)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{"Select cutoff message"}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[420px] overflow-y-auto flex flex-col gap-2">
          {isPending && <Loading />}
          {!isPending &&
            data?.messages.map((message, index) => {
              const isSelected = selectedMessageId === message.sourceId
              const hasNegativeRatings = message.negativeRatings > 0

              return (
                <button
                  type="button"
                  key={message.sourceId}
                  className={cn(
                    "text-left rounded-sm border p-2 text-xs flex flex-col gap-1",
                    isSelected && "border-primary bg-primary/10",
                    hasNegativeRatings && !isSelected && "border-destructive bg-destructive/10",
                  )}
                  onClick={() => setSelectedMessageId(message.sourceId)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {"#"}
                      {index + 1}
                      {" · "}
                      {message.role}
                    </span>
                    {hasNegativeRatings && (
                      <span className="text-destructive font-medium">
                        {message.negativeRatings}
                        {" negative"}
                      </span>
                    )}
                  </div>
                  <span className="text-muted-foreground line-clamp-3">
                    {message.content ?? "[non-text message]"}
                  </span>
                </button>
              )
            })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {"Cancel"}
          </Button>
          <Button disabled={!selectedMessageId} onClick={downloadSnapshot}>
            <Download className="size-4 shrink-0" />
            {"Download JSON"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
